import { ReactElement, useCallback } from 'react'
import { Box, Stack, TextField, Typography } from '@mui/material'
import PlanElementFormHeader from '@editor/components/PlanElementFormHeader'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import usePlanTableMutations from '@editor/hooks/usePlanTableMutations'
import usePlanViewPlanTables from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import { DeleteButton } from '@ui/actions'
import { usePlanTableStore } from '@editorStores'
import { usePlanTableLayoutMetrics, useSelectedPlanElementId } from '@editorHooks'
import { isTemplatePlanTable } from 'src/domain/planTables/types'
import PlanExportConfigFields from './PlanExportConfigFields'
import PlanTableDataEditor from './PlanTableDataEditor'
import PlanTablePositionAnchorFields from './PlanTablePositionAnchorFields'
import PlanTableSortingFields from './PlanTableSortingFields'
import PlanTableTemplateOptionsFields from './PlanTableTemplateOptionsFields'

interface Props {
  /** Shows a close button in the form's header when given. */
  onClose?: () => void
}

const PlanTableForm = ({ onClose }: Props): ReactElement => {
  const selectedId = useSelectedPlanElementId('table')
  const planTable = usePlanTableStore(state =>
    selectedId ? state.tables.find(table => table.id === selectedId) : undefined,
  )
  const { updatePlanTableFields, updatePlanTableData, deletePlanTable } = usePlanTableMutations()
  const { exitPlanTableEdit } = usePlanTableEditMode()
  const layoutMetrics = usePlanTableLayoutMetrics()
  const { getTableData } = usePlanViewPlanTables()

  const handleDeleteTable = useCallback(() => {
    if (!planTable) return
    deletePlanTable(planTable.id)
    exitPlanTableEdit()
  }, [deletePlanTable, exitPlanTableEdit, planTable])

  if (!planTable) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Keine Tabelle ausgewählt.
        </Typography>
      </Box>
    )
  }

  const isTemplateTable = planTable ? isTemplatePlanTable(planTable) : false

  return (
    <Box>
      <PlanElementFormHeader
        title={<span data-cy="plan-table-form-title">Tabelle bearbeiten</span>}
        onClose={onClose}
      />
      <Stack spacing={1} p={1}>
        {isTemplateTable && (
          <Typography variant="body2" color="text.secondary" data-cy="plan-table-template-info">
            Vorlagentabelle – Inhalte werden aus Projektdaten erzeugt und sind hier
            schreibgeschützt.
          </Typography>
        )}

        <TextField
          label="Name"
          size="small"
          value={planTable.name}
          onChange={event => updatePlanTableFields(planTable.id, { name: event.target.value })}
          fullWidth
          data-cy="plan-table-name-input"
        />

        <PlanTablePositionAnchorFields
          position={planTable.position}
          positionAnchor={planTable.positionAnchor}
          table={getTableData(planTable)}
          layoutMetrics={layoutMetrics}
          onChange={changes => updatePlanTableFields(planTable.id, changes)}
        />

        {/* Custom tables are edited in their stored row order; the sorting applies to the plan. */}
        <PlanTableDataEditor
          table={isTemplateTable ? getTableData(planTable) : planTable.table}
          readOnly={isTemplateTable}
          onTableChange={updater => updatePlanTableData(planTable.id, updater)}
        />

        {isTemplateTable && (
          <PlanTableTemplateOptionsFields
            templateKey={planTable.templateKey}
            templateOptions={planTable.templateOptions}
            onChange={templateOptions => updatePlanTableFields(planTable.id, { templateOptions })}
          />
        )}

        <PlanExportConfigFields
          planConfig={planTable.planConfig}
          onChange={planConfig => updatePlanTableFields(planTable.id, { planConfig })}
        />

        <PlanTableSortingFields
          table={getTableData(planTable)}
          sorting={planTable.sorting}
          onChange={sorting => updatePlanTableFields(planTable.id, { sorting })}
        />

        <Stack direction="row" spacing={1}>
          <DeleteButton onClick={handleDeleteTable} size="small" data-cy="plan-table-delete-button">
            Tabelle löschen
          </DeleteButton>
        </Stack>
      </Stack>
    </Box>
  )
}

export default PlanTableForm
